import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, RangeSlider, Select, Text, Group, Stack } from "@mantine/core";
import { useCallback, useState } from "react";
import { useAppAction } from "../../hooks/redux.js";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { changeLayerConfig } from "../../reducers/layersReducer.js";
import { FormattedMessage } from "react-intl";
import { LayerPaintSettings } from "./LayerPaintSettings.js";
import { useDebouncedCallback } from "@mantine/hooks";
import { isEqual } from "es-toolkit/compat";
function getVisibility(layer) {
    const minZoom = layer.minzoom ?? 0;
    const maxZoom = layer.maxzoom ?? 22;
    return [minZoom, maxZoom];
}
function setVisibility(layer, value) {
    const [minZoom, maxZoom] = value;
    layer.minzoom = minZoom;
    layer.maxzoom = maxZoom;
}
export const LayerStyleSettings = ({ layer }) => {
    const { id: layerId, paint, type } = layer;
    const [visibility, setNewVisibility] = useState(() => getVisibility(layer));
    const captureEvent = useCaptureEvent();
    const updateLayerConfig = useAppAction(changeLayerConfig);
    const handleVisualizationChange = useCallback((value) => {
        captureEvent(Event.ChangeLayerConfig, {
            layerId,
            property: "type",
            value,
        });
    }, [layerId, captureEvent]);
    const debouncedUpdate = useDebouncedCallback((next) => {
        const layerVisibility = getVisibility(layer);
        if (!isEqual(layerVisibility, next)) {
            const updatedLayer = structuredClone(layer);
            setVisibility(updatedLayer, next);
            captureEvent(Event.ChangeLayerConfig, {
                layerId,
                property: "zoom range",
                value: next,
            });
            updateLayerConfig({
                id: layerId,
                config: updatedLayer,
                type: "zoom",
            });
        }
    }, 200);
    const currentType = layer.type;
    return (_jsxs(Stack, { gap: "md", px: "sm", style: { overflow: "hidden" }, children: [_jsxs(Stack, { gap: "xs", pb: "md", children: [_jsx(Text, { fz: "sm", fw: 600, children: _jsx(FormattedMessage, { id: "label.layer", defaultMessage: "Layer" }) }), _jsxs(Stack, { gap: 8, children: [_jsx(Text, { fz: "sm", children: _jsx(FormattedMessage, { id: "label.visualization", defaultMessage: "Visualization" }) }), _jsx(Select, { size: "xs", value: currentType, onChange: handleVisualizationChange, data: [
                                    { value: "fill", label: "Polygon" },
                                    { value: "line", label: "Line" },
                                    { value: "symbol", label: "Symbol" },
                                    { value: "circle", label: "Circle" },
                                    { value: "raster", label: "Raster" },
                                    { value: "heatmap", label: "Heatmap" },
                                    { value: "hillshade", label: "Hillshade" },
                                    { value: "color-relief", label: "Color relief" },
                                    { value: "background", label: "Background" },
                                    { value: "fill-extrusion", label: "Fill extrusion" },
                                ] })] }), _jsxs(Stack, { gap: 8, children: [_jsxs(Group, { justify: "space-between", children: [_jsx(Text, { fz: "sm", children: _jsx(FormattedMessage, { id: "label.visibility", defaultMessage: "Visibility" }) }), _jsx(Text, { fz: "sm", children: `${visibility[0]}-${visibility[1]}` })] }), _jsx(RangeSlider, { min: 0, max: 22, step: 1, size: "xs", showLabelOnHover: false, value: visibility, onChange: (next) => {
                                    setNewVisibility(next);
                                    debouncedUpdate(next);
                                }, minRange: 1 })] })] }), _jsx(Divider, {}), _jsx(LayerPaintSettings, { layerId: layerId, paint: paint, type: type })] }));
};
